import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MainLayout } from '../components/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getDefaultVault, loadVaultItems } from '../api/vault';
import {
  Sliders,
  Palette,
  Moon,
  Sun,
  Laptop,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Key,
  Clock,
  Monitor,
  Smartphone,
  Download,
  Upload,
  Copy,
  Check,
  ExternalLink,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  X,
  Lock,
  LogOut,
  Eye,
  EyeOff,
  Database,
  User,
  Sparkles,
  ChevronRight,
  Layers,
  Zap,
  Info,
  LayoutGrid,
  UploadCloud,
  History,
  Phone,
  MessageSquare,
} from 'lucide-react';
import PinnedAppsSettings from '../components/PinnedAppsSettings';
import ApplicationSecuritySettings from '../components/security/ApplicationSecuritySettings';
import ImportWizard from '../components/ImportWizard';
import ImportHistoryTable from '../components/ImportHistoryTable';
import { verifyExportStepUp } from '../api/importExport';
import { VaultCrypto } from '@password-manager/crypto';
import { WebCryptoProvider } from '../crypto/WebCryptoProvider';

export default function Settings() {
  const { 
    user, 
    logout, 
    vaultKey,
    clipboardClearTimeoutMs, 
    setClipboardClearTimeoutMs 
  } = useAuth();
  
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, resolvedTheme, setTheme } = useTheme();

  // Active navigation tab (supports ?tab= query parameter)
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('tab') || new URLSearchParams(window.location.search).get('section');
      if (p) return p;
    } catch {}
    return 'appearance';
  });

  useEffect(() => {
    try {
      const p = new URLSearchParams(location.search).get('tab') || new URLSearchParams(location.search).get('section');
      if (p) setActiveTab(p);
    } catch {}
  }, [location.search]);

  // Display Name state
  const [displayName, setDisplayName] = useState(() => {
    return window.localStorage.getItem('ciphervault_display_name') || user?.email?.split('@')[0] || 'Vault Master';
  });
  const [savedName, setSavedName] = useState(false);

  // Auto-lock state (Deactivated by default)
  const [autoLockEnabled, setAutoLockEnabled] = useState(() => {
    return window.localStorage.getItem('cipher-vault-autolock-enabled') === 'true';
  });
  const [autoLockTimeout, setAutoLockTimeout] = useState(() => {
    return window.localStorage.getItem('cipher-vault-autolock') || '15';
  });

  // UI Density & Preferences
  const [density, setDensity] = useState(() => {
    return window.localStorage.getItem('cipher-vault-density') || 'comfortable';
  });

  const [maskPasswords, setMaskPasswords] = useState(() => {
    return window.localStorage.getItem('cipher-vault-mask-preview') !== 'false';
  });

  const [actionToasts, setActionToasts] = useState(() => {
    return window.localStorage.getItem('cipher-vault-toasts') !== 'false';
  });

  // Export states
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('encrypted');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [stepUpPassword, setStepUpPassword] = useState('');

  // Import state
  const [importModalOpen, setImportModalOpen] = useState(false);

  // Copy feedback states
  const [copiedUid, setCopiedUid] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);

  // Toast feedback
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ─────────────────────────────────────────────
  // CipherChat Module Activation State
  // ─────────────────────────────────────────────
  const COUNTRY_CODES = [
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+1', country: 'United States / Canada', flag: '🇺🇸' },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
    { code: '+971', country: 'UAE', flag: '🇦🇪' },
    { code: '+65', country: 'Singapore', flag: '🇸🇬' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' },
    { code: '+86', country: 'China', flag: '🇨🇳' },
    { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  ];

  const [chatProfile, setChatProfile] = useState(() => {
    try {
      const saved = localStorage.getItem('ciphervault_chat_profile');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return {
      countryCode: '+91',
      phoneNumber: '',
      isActivated: false,
      isLocked: false,
      activatedAt: null,
    };
  });

  const [selectedCountryCode, setSelectedCountryCode] = useState(chatProfile.countryCode || '+91');
  const [chatPhoneNumber, setChatPhoneNumber] = useState(chatProfile.phoneNumber || '');
  const [chatPhoneError, setChatPhoneError] = useState('');
  const [isActivatingChat, setIsActivatingChat] = useState(false);

  const handleActivateChatModule = (e) => {
    e.preventDefault();
    if (chatProfile.isLocked) {
      showToast('Mobile number is already permanently locked.', 'warning');
      return;
    }

    const cleaned = chatPhoneNumber.replace(/\D/g, '');
    if (!cleaned || cleaned.length < 7 || cleaned.length > 15) {
      setChatPhoneError('Please enter a valid mobile number (7 to 15 digits).');
      return;
    }

    if (selectedCountryCode === '+91' && cleaned.length !== 10) {
      setChatPhoneError('Indian mobile numbers must be exactly 10 digits.');
      return;
    }

    setChatPhoneError('');
    setIsActivatingChat(true);

    // Direct validate & authenticate without SMS OTP
    setTimeout(() => {
      const updated = {
        countryCode: selectedCountryCode,
        phoneNumber: cleaned,
        fullNumber: `${selectedCountryCode} ${cleaned}`,
        isActivated: true,
        isLocked: true, // One-time save: cannot be modified after this
        activatedAt: new Date().toISOString(),
        email: user?.email || 'admin@nikhil.com',
        isEmailVerified: true,
      };
      localStorage.setItem('ciphervault_chat_profile', JSON.stringify(updated));
      setChatProfile(updated);
      setIsActivatingChat(false);
      showToast('CipherChat Module activated! Mobile number permanently registered and locked.', 'success');
    }, 450);
  };

  // Clipboard options
  const clipboardOptions = [
    { label: '15 seconds (Strict / Recommended)', value: 15000 },
    { label: '30 seconds', value: 30000 },
    { label: '60 seconds (1 minute)', value: 60000 },
    { label: '2 minutes', value: 120000 },
    { label: '5 minutes', value: 300000 },
    { label: 'Never (Insecure)', value: 0 },
  ];

  // Save display name
  const handleSaveDisplayName = (e) => {
    e.preventDefault();
    if (!displayName.trim()) return;
    window.localStorage.setItem('ciphervault_display_name', displayName.trim());
    setSavedName(true);
    showToast('Display profile name saved successfully.');
    setTimeout(() => setSavedName(false), 2500);
  };

  // Auto-lock toggle (Active / Deactive)
  const handleToggleAutoLockEnabled = () => {
    const next = !autoLockEnabled;
    setAutoLockEnabled(next);
    window.localStorage.setItem('cipher-vault-autolock-enabled', String(next));
    showToast(`Vault Inactivity Auto-Lock ${next ? 'activated' : 'deactivated'}.`);
  };

  // Auto-lock update
  const handleAutoLockChange = (e) => {
    const val = e.target.value;
    setAutoLockTimeout(val);
    window.localStorage.setItem('cipher-vault-autolock', val);
    showToast(`Inactivity auto-lock timeout set to ${val === '0' ? 'Never' : `${val} minutes`}.`);
  };

  // Density update
  const handleDensityChange = (newDensity) => {
    setDensity(newDensity);
    window.localStorage.setItem('cipher-vault-density', newDensity);
    showToast(`Interface density switched to ${newDensity}.`);
  };

  // Mask passwords toggle
  const handleToggleMask = () => {
    const next = !maskPasswords;
    setMaskPasswords(next);
    window.localStorage.setItem('cipher-vault-mask-preview', String(next));
    showToast(`Password preview masking ${next ? 'enabled' : 'disabled'}.`);
  };

  // Toasts toggle
  const handleToggleToasts = () => {
    const next = !actionToasts;
    setActionToasts(next);
    window.localStorage.setItem('cipher-vault-toasts', String(next));
    showToast(`Action notifications ${next ? 'enabled' : 'disabled'}.`);
  };

  // Copy helpers
  const handleCopyUid = () => {
    if (!user?.id) return;
    navigator.clipboard.writeText(user.id);
    setCopiedUid(true);
    setTimeout(() => setCopiedUid(false), 2000);
  };

  const handleCopyEmail = () => {
    if (!user?.email) return;
    navigator.clipboard.writeText(user.email);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  // Purge local caches
  const handlePurgeCaches = () => {
    try {
      const preserveKeys = ['ciphervault-theme', 'ciphervault_display_name', 'cipher-vault-autolock', 'pm_user_email'];
      const toRemove = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && !preserveKeys.includes(key)) {
          toRemove.push(key);
        }
      }
      toRemove.forEach((k) => window.localStorage.removeItem(k));
      showToast('Transient local cache and keys purged safely.');
    } catch {
      showToast('Failed to purge local cache', 'error');
    }
  };

  // Export Vault Data
  const handleExecuteExport = async () => {
    setExporting(true);
    setExportError('');
    try {
      if (!vaultKey) {
        throw new Error('Vault is locked. Please unlock your vault to decrypt and export data.');
      }

      // Step-up verification for plaintext formats
      if (exportFormat === 'json' || exportFormat === 'csv') {
        if (!stepUpPassword) {
          throw new Error('Master password is required to authorize plaintext export.');
        }
        const vaultCrypto = new VaultCrypto(WebCryptoProvider);
        const authKey = await vaultCrypto.generateAuthKey(stepUpPassword, user?.email);
        await verifyExportStepUp(authKey, exportFormat);
      }

      const items = await loadVaultItems(vaultKey);
      if (!items || items.length === 0) {
        showToast('Vault contains no items to export.', 'warning');
        setExportModalOpen(false);
        setExporting(false);
        return;
      }

      const dateStr = new Date().toISOString().split('T')[0];
      let fileContent = '';
      let mimeType = 'application/json';
      let fileName = `ciphervault-backup-${dateStr}.json`;

      if (exportFormat === 'encrypted') {
        mimeType = 'application/json';
        fileName = `ciphervault-encrypted-backup-${dateStr}.json`;
        const exportObj = {
          version: 1,
          type: 'ENCRYPTED_CIPHERVAULT_BACKUP',
          generator: 'CipherVault Pro',
          exportedAt: new Date().toISOString(),
          account: user?.email,
          itemCount: items.length,
          items: items.map((it) => ({
            id: it.id,
            itemType: it.item_type,
            encryptedData: it.encrypted_data,
            isFavorite: it.is_favorite,
            createdAt: it.created_at,
            updatedAt: it.updated_at,
          })),
        };
        fileContent = JSON.stringify(exportObj, null, 2);
      } else if (exportFormat === 'json') {
        const exportObj = {
          version: 1,
          type: 'PLAINTEXT_JSON',
          generator: 'CipherVault Pro',
          exportedAt: new Date().toISOString(),
          account: user?.email,
          itemCount: items.length,
          items: items.map((it) => ({
            id: it.id,
            itemType: it.item_type,
            title: it.plaintext?.title || it.title || 'Untitled',
            username: it.plaintext?.username || '',
            password: it.plaintext?.password || '',
            website: it.plaintext?.website || it.plaintext?.url || '',
            notes: it.plaintext?.notes || '',
            category: it.plaintext?.category || it.category_id || 'Default',
            createdAt: it.created_at,
            updatedAt: it.updated_at,
          })),
        };
        fileContent = JSON.stringify(exportObj, null, 2);
      } else {
        // CSV Format
        mimeType = 'text/csv;charset=utf-8;';
        fileName = `ciphervault-backup-${dateStr}.csv`;
        const headers = ['Title', 'ItemType', 'Username', 'Password', 'Website', 'Category', 'Notes'];
        const rows = items.map((it) => {
          const escape = (str) => `"${String(str || '').replace(/"/g, '""')}"`;
          return [
            escape(it.plaintext?.title || it.title || 'Untitled'),
            escape(it.item_type || 'PASSWORD'),
            escape(it.plaintext?.username || ''),
            escape(it.plaintext?.password || ''),
            escape(it.plaintext?.website || it.plaintext?.url || ''),
            escape(it.plaintext?.category || 'Default'),
            escape(it.plaintext?.notes || ''),
          ].join(',');
        });
        fileContent = [headers.join(','), ...rows].join('\r\n');
      }

      const blob = new Blob([fileContent], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setExportModalOpen(false);
      setStepUpPassword('');
      showToast(`Exported ${items.length} items to ${fileName} successfully!`);
    } catch (err) {
      setExportError(err.response?.data?.message || err.message || 'Failed to export vault data.');
    } finally {
      setExporting(false);
    }
  };

  // Derived user initials
  const userInitials = useMemo(() => {
    if (displayName) {
      const parts = displayName.trim().split(' ');
      if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      return displayName.substring(0, 2).toUpperCase();
    }
    if (user?.email) return user.email.substring(0, 2).toUpperCase();
    return 'CV';
  }, [displayName, user]);

  return (
    <MainLayout>
      {/* Toast Alert */}
      {toast && (
        <div className={`admin-toast admin-toast-${toast.type}`}>
          {toast.type === 'error' && <AlertTriangle size={18} />}
          {toast.type === 'warning' && <ShieldAlert size={18} />}
          {toast.type === 'success' && <CheckCircle2 size={18} />}
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="toast-close-btn" aria-label="Close message">
            <X size={15} />
          </button>
        </div>
      )}

      {/* Pro Header - Full Bleed Edge-to-Edge */}
      <div className="settings-header-pro notes-hero-banner">
        <div className="notes-hero-content">
          <div className="settings-header-titles">
            <div className="settings-badge-top notes-hero-badge">
              <Sliders size={14} /> Preferences & System Hub
            </div>
            <h1 className="settings-page-title notes-hero-title">Settings & Preferences</h1>
            <p className="settings-page-subtitle notes-hero-subtitle">
              Manage your interface appearance, personal account profile, memory privacy guards, inactivity lock timers, and vault backups.
            </p>
          </div>
          <div className="settings-header-actions">
            <button className="btn-secondary notes-hero-btn-secondary" onClick={() => navigate('/security')} title="Security Center">
              <ShieldCheck size={16} color="#0d9488" />
              <span>Security Center</span>
            </button>
            <button className="btn-danger-outline notes-hero-btn-danger" onClick={logout} title="Lock and close vault session">
              <Lock size={15} />
              <span>Lock Vault</span>
            </button>
          </div>
        </div>
      </div>

      <div className="settings-page-container">

        {/* Tab Navigation Pill Bar */}
        <div className="settings-tab-bar">
          <button
            className={`settings-tab-btn ${activeTab === 'appearance' ? 'active' : ''}`}
            onClick={() => setActiveTab('appearance')}
          >
            <Palette size={16} />
            <span>Appearance & Themes</span>
          </button>
          <button
            className={`settings-tab-btn ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            <User size={16} />
            <span>Profile & Account</span>
          </button>
          <button
            className={`settings-tab-btn ${activeTab === 'privacy' ? 'active' : ''}`}
            onClick={() => setActiveTab('privacy')}
          >
            <Shield size={16} />
            <span>Privacy & Auto-Lock</span>
          </button>
          <button
            className={`settings-tab-btn ${activeTab === 'data' ? 'active' : ''}`}
            onClick={() => setActiveTab('data')}
          >
            <Database size={16} />
            <span>Vault Backup & Export</span>
          </button>
          <button
            className={`settings-tab-btn ${activeTab === 'shortcuts' ? 'active' : ''}`}
            onClick={() => setActiveTab('shortcuts')}
          >
            <Sparkles size={16} />
            <span>Shortcuts & Danger</span>
          </button>
          <button
            className={`settings-tab-btn ${activeTab === 'apps' ? 'active' : ''}`}
            onClick={() => setActiveTab('apps')}
          >
            <LayoutGrid size={16} />
            <span>Applications & Pinned</span>
          </button>
          <button
            className={`settings-tab-btn ${activeTab === 'app-security' ? 'active' : ''}`}
            onClick={() => setActiveTab('app-security')}
          >
            <ShieldCheck size={16} />
            <span>Application Security</span>
          </button>
          <button
            className={`settings-tab-btn ${activeTab === 'import' ? 'active' : ''}`}
            onClick={() => setActiveTab('import')}
          >
            <UploadCloud size={16} />
            <span>Import & Export</span>
          </button>
          <button
            className={`settings-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <History size={16} />
            <span>Import History</span>
          </button>
        </div>

        {/* ══════════════════════════════════════════════════════════
            TAB 1: APPEARANCE & THEMES
            ══════════════════════════════════════════════════════════ */}
        {activeTab === 'appearance' && (
          <div className="settings-tab-content">
            {/* Visual Theme Cards */}
            <div className="settings-section-card glass-panel">
              <div className="settings-section-header">
                <div className="section-icon-badge" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
                  <Palette size={22} />
                </div>
                <div>
                  <h3 className="section-title">Color Palette & Visual Theme</h3>
                  <p className="section-subtitle">
                    Select your preferred visual mode. Changes take effect across your entire workspace immediately.
                  </p>
                </div>
              </div>

              <div className="theme-selection-grid">
                {/* Dark Theme Card */}
                <div 
                  className={`theme-card-visual ${theme === 'dark' ? 'active' : ''}`}
                  onClick={() => { setTheme('dark'); showToast('Cyber Dark theme activated.'); }}
                >
                  <div className="theme-card-preview dark-preview">
                    <div className="preview-sidebar-mock" />
                    <div className="preview-main-mock">
                      <div className="preview-topbar-mock" />
                      <div className="preview-content-mock">
                        <div className="mock-card-1" />
                        <div className="mock-card-2" />
                      </div>
                    </div>
                  </div>
                  <div className="theme-card-info">
                    <div className="theme-card-title-row">
                      <Moon size={18} className="theme-icon" />
                      <strong>Cyber Dark</strong>
                      {theme === 'dark' && <span className="active-pill"><Check size={12} /> Active</span>}
                    </div>
                    <p>Ultra-deep slate palette with teal accents, crafted for zero eye-strain and high contrast.</p>
                  </div>
                </div>

                {/* Light Theme Card */}
                <div 
                  className={`theme-card-visual ${theme === 'light' ? 'active' : ''}`}
                  onClick={() => { setTheme('light'); showToast('Crisp Light theme activated.'); }}
                >
                  <div className="theme-card-preview light-preview">
                    <div className="preview-sidebar-mock" />
                    <div className="preview-main-mock">
                      <div className="preview-topbar-mock" />
                      <div className="preview-content-mock">
                        <div className="mock-card-1" />
                        <div className="mock-card-2" />
                      </div>
                    </div>
                  </div>
                  <div className="theme-card-info">
                    <div className="theme-card-title-row">
                      <Sun size={18} className="theme-icon" />
                      <strong>Crisp Light</strong>
                      {theme === 'light' && <span className="active-pill"><Check size={12} /> Active</span>}
                    </div>
                    <p>Clean white and subtle cloud tones with modern sapphire accents for bright daytime environments.</p>
                  </div>
                </div>

                {/* System Default Card */}
                <div 
                  className={`theme-card-visual ${theme === 'system' ? 'active' : ''}`}
                  onClick={() => { setTheme('system'); showToast('Theme synchronized with system preferences.'); }}
                >
                  <div className="theme-card-preview system-preview">
                    <div className="preview-half-dark">
                      <div className="preview-sidebar-mock" />
                    </div>
                    <div className="preview-half-light">
                      <div className="preview-main-mock" />
                    </div>
                  </div>
                  <div className="theme-card-info">
                    <div className="theme-card-title-row">
                      <Laptop size={18} className="theme-icon" />
                      <strong>System Auto</strong>
                      {theme === 'system' && <span className="active-pill"><Check size={12} /> Active</span>}
                    </div>
                    <p>Automatically synchronizes with your device operating system dark / light mode schedule.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Layout Density & Visual Controls */}
            <div className="settings-section-card glass-panel">
              <div className="settings-section-header">
                <div className="section-icon-badge" style={{ background: 'rgba(13, 148, 136, 0.12)', color: '#0d9488' }}>
                  <Layers size={22} />
                </div>
                <div>
                  <h3 className="section-title">Layout & Interface Preferences</h3>
                  <p className="section-subtitle">Fine-tune spacing, list density, and preview behaviors.</p>
                </div>
              </div>

              <div className="settings-options-list">
                {/* Density Row */}
                <div className="settings-option-item">
                  <div className="option-info">
                    <strong>Interface Spacing & Density</strong>
                    <p>Choose between standard comfortable spacing or compact data density for high-volume vaults.</p>
                  </div>
                  <div className="option-control">
                    <div className="segmented-control">
                      <button 
                        type="button"
                        className={`seg-btn ${density === 'comfortable' ? 'active' : ''}`}
                        onClick={() => handleDensityChange('comfortable')}
                      >
                        Comfortable
                      </button>
                      <button 
                        type="button"
                        className={`seg-btn ${density === 'compact' ? 'active' : ''}`}
                        onClick={() => handleDensityChange('compact')}
                      >
                        Compact
                      </button>
                    </div>
                  </div>
                </div>

                {/* Mask Password Previews */}
                <div className="settings-option-item">
                  <div className="option-info">
                    <strong>Mask Credentials in Vault Lists</strong>
                    <p>Conceal password characters and sensitive keys with bullets (••••••••) in lists and previews by default.</p>
                  </div>
                  <div className="option-control">
                    <button 
                      type="button" 
                      className={`toggle-switch ${maskPasswords ? 'on' : 'off'}`}
                      onClick={handleToggleMask}
                      aria-label="Toggle password masking"
                    >
                      <span className="toggle-thumb" />
                    </button>
                  </div>
                </div>

                {/* Animated Feedback Toasts */}
                <div className="settings-option-item">
                  <div className="option-info">
                    <strong>Interactive Action Notifications</strong>
                    <p>Display floating animated toasts when copying passwords, saving changes, or updating preferences.</p>
                  </div>
                  <div className="option-control">
                    <button 
                      type="button" 
                      className={`toggle-switch ${actionToasts ? 'on' : 'off'}`}
                      onClick={handleToggleToasts}
                      aria-label="Toggle action toasts"
                    >
                      <span className="toggle-thumb" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            TAB 2: PROFILE & ACCOUNT
            ══════════════════════════════════════════════════════════ */}
        {activeTab === 'account' && (
          <div className="settings-tab-content">
            {/* Account Hero Profile Card */}
            <div className="settings-section-card glass-panel">
              <div className="profile-hero-layout">
                <div className="profile-hero-avatar">
                  <span>{userInitials}</span>
                  <div className="avatar-badge-online" title="Vault Online" />
                </div>
                <div className="profile-hero-details">
                  <div className="profile-hero-badge-row">
                    <span className="role-tag-chip">
                      {user?.role === 'SUPER_ADMIN' ? 'Super Administrator' : user?.role || 'Vault Owner'}
                    </span>
                    <span className="verified-account-tag">
                      <ShieldCheck size={13} /> Zero-Knowledge Verified
                    </span>
                  </div>
                  <h2 className="profile-hero-name">{displayName}</h2>
                  <div className="profile-meta-row">
                    <span className="profile-email-text">{user?.email || 'user@ciphervault.com'}</span>
                    <button type="button" onClick={handleCopyEmail} className="btn-icon-subtle" title="Copy email address">
                      {copiedEmail ? <Check size={13} color="#10b981" /> : <Copy size={13} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Profile Customization Form */}
              <form onSubmit={handleSaveDisplayName} className="profile-edit-form">
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="display-name-input">Display / Workspace Name</label>
                  <input
                    id="display-name-input"
                    type="text"
                    className="input-field"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter your nickname or workspace name"
                    maxLength={50}
                  />
                </div>
                <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-end', height: '42px' }}>
                  {savedName ? <><Check size={15} /> Saved</> : 'Save Display Name'}
                </button>
              </form>

              {/* Account UUID Details */}
              <div className="account-details-grid">
                <div className="detail-stat-tile">
                  <span className="detail-label">Unique User Identifier (UUID)</span>
                  <div className="detail-value-copy-row">
                    <code>{user?.id || '—'}</code>
                    <button type="button" onClick={handleCopyUid} className="btn-ghost btn-sm" title="Copy UUID">
                      {copiedUid ? <Check size={13} color="#10b981" /> : <Copy size={13} />}
                    </button>
                  </div>
                </div>

                <div className="detail-stat-tile">
                  <span className="detail-label">Account Created At</span>
                  <span className="detail-value-text">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : 'Active Session'}
                  </span>
                </div>

                <div className="detail-stat-tile">
                  <span className="detail-label">Client Cryptography Engine</span>
                  <span className="detail-value-text text-accent">
                    Argon2id + AES-256-GCM (Hardware Accelerated)
                  </span>
                </div>

                <div className="detail-stat-tile">
                  <span className="detail-label">Vault Master Key</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="status-dot-active" />
                    <span className="detail-value-text">Decrypted in memory</span>
                    <button type="button" className="link-action-btn" onClick={() => navigate('/security')}>
                      Manage in Security <ChevronRight size={13} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ── CipherChat Module Activation Card ── */}
            <div className="settings-section-card glass-panel" id="cipherchat-activation-section">
              <div className="settings-section-header">
                <div className="section-icon-badge" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
                  <MessageSquare size={22} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                    <h3 className="section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      CipherChat Module Activation
                      {chatProfile.isActivated ? (
                        <span className="badge-pill-success" style={{ fontSize: '11px', padding: '3px 8px', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', borderRadius: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <CheckCircle2 size={12} /> Active & Verified
                        </span>
                      ) : (
                        <span className="badge-pill-warning" style={{ fontSize: '11px', padding: '3px 8px', background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', borderRadius: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <AlertTriangle size={12} /> Activation Required
                        </span>
                      )}
                    </h3>
                    {chatProfile.isActivated && (
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        onClick={() => navigate('/messages')}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '6px 14px', background: '#10b981', borderColor: '#10b981' }}
                      >
                        <MessageSquare size={14} /> Open Messenger
                      </button>
                    )}
                  </div>
                  <p className="section-subtitle" style={{ marginTop: '4px' }}>
                    A registered mobile number and verified email are mandatory to activate CipherChat. Once saved, this phone number serves as your permanent chat identity and <strong>cannot be changed</strong>.
                  </p>
                </div>
              </div>

              {/* Status checklist */}
              <div className="chat-activation-status-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', margin: '16px 0' }}>
                <div style={{ padding: '12px', borderRadius: '10px', background: 'var(--bg-card-subtle, rgba(0,0,0,0.03))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Check size={16} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', fontWeight: 600 }}>Email Verification</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{user?.email || 'admin@nikhil.com'} (Verified)</div>
                  </div>
                </div>

                <div style={{ padding: '12px', borderRadius: '10px', background: 'var(--bg-card-subtle, rgba(0,0,0,0.03))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: chatProfile.isActivated ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)', color: chatProfile.isActivated ? '#10b981' : '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {chatProfile.isActivated ? <Check size={16} /> : <Phone size={16} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', fontWeight: 600 }}>Mobile Number Binding</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      {chatProfile.isActivated ? `${chatProfile.fullNumber || chatProfile.phoneNumber} (Locked)` : 'Compulsory for Chat'}
                    </div>
                  </div>
                </div>
              </div>

              {chatProfile.isLocked ? (
                /* Permanent Locked State */
                <div className="chat-locked-box" style={{ padding: '16px', borderRadius: '12px', background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.25)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#10b981', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Lock size={20} />
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{chatProfile.fullNumber || `${chatProfile.countryCode} ${chatProfile.phoneNumber}`}</span>
                        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '8px', background: 'rgba(16, 185, 129, 0.2)', color: '#059669', fontWeight: 600 }}>
                          🔒 Permanent Chat ID
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                        Directly authenticated. Number cannot be changed because it is permanently bound to your encrypted chat directory.
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => navigate('/messages')}
                    style={{ background: '#10b981', borderColor: '#10b981' }}
                  >
                    <MessageSquare size={16} /> Open CipherChat
                  </button>
                </div>
              ) : (
                /* Direct Authenticate & Save Form */
                <form onSubmit={handleActivateChatModule} className="chat-phone-form" style={{ marginTop: '12px' }}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    {/* Country Code Select */}
                    <div style={{ width: '180px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-secondary)' }}>
                        Country Code (Default +91)
                      </label>
                      <select
                        className="input-field"
                        value={selectedCountryCode}
                        onChange={(e) => setSelectedCountryCode(e.target.value)}
                        style={{ height: '42px' }}
                      >
                        {COUNTRY_CODES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.flag} {c.code} ({c.country})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Phone Number Input */}
                    <div style={{ flex: 1, minWidth: '220px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-secondary)' }}>
                        Mobile Phone Number <span style={{ color: 'var(--danger, #ef4444)' }}>*</span>
                      </label>
                      <div style={{ position: 'relative' }}>
                        <Phone size={16} style={{ position: 'absolute', left: 12, top: 13, color: 'var(--text-secondary)' }} />
                        <input
                          type="tel"
                          className="input-field"
                          value={chatPhoneNumber}
                          onChange={(e) => {
                            setChatPhoneNumber(e.target.value);
                            if (chatPhoneError) setChatPhoneError('');
                          }}
                          placeholder={selectedCountryCode === '+91' ? '9876543210 (10 digits)' : 'Enter mobile number'}
                          style={{ paddingLeft: '38px', height: '42px' }}
                          maxLength={15}
                          autoComplete="tel"
                        />
                      </div>
                      {chatPhoneError && (
                        <div style={{ fontSize: '12px', color: 'var(--danger, #ef4444)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <AlertTriangle size={13} /> {chatPhoneError}
                        </div>
                      )}
                    </div>

                    {/* Submit Button */}
                    <div style={{ alignSelf: 'flex-end' }}>
                      <button
                        type="submit"
                        disabled={isActivatingChat || !chatPhoneNumber.trim()}
                        className="btn-primary"
                        style={{ height: '42px', padding: '0 20px', background: '#10b981', borderColor: '#10b981', color: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                      >
                        {isActivatingChat ? (
                          <><RefreshCw size={15} className="spin" /> Validating...</>
                        ) : (
                          <><CheckCircle2 size={16} /> Direct Authenticate & Save</>
                        )}
                      </button>
                    </div>
                  </div>

                  <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Info size={14} color="#3b82f6" />
                    <span>Direct authentication without OTP. Format validated directly. <strong>Notice:</strong> One-time save restriction; once registered, the number cannot be changed.</span>
                  </div>
                </form>
              )}
            </div>

            {/* Architecture Overview Card */}
            <div className="settings-section-card glass-panel">
              <div className="settings-section-header">
                <div className="section-icon-badge" style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10b981' }}>
                  <ShieldCheck size={22} />
                </div>
                <div>
                  <h3 className="section-title">End-to-End Cryptographic Isolation</h3>
                  <p className="section-subtitle">
                    How CipherVault ensures that even system administrators cannot inspect your encrypted records.
                  </p>
                </div>
              </div>

              <div className="security-guarantee-box">
                <div className="guarantee-point">
                  <div className="point-icon"><Key size={16} /></div>
                  <div>
                    <strong>Master Password Never Leaves Device</strong>
                    <p>Your master password is never transmitted across the network. Only an Argon2id-derived authentication token reaches the API.</p>
                  </div>
                </div>

                <div className="guarantee-point">
                  <div className="point-icon"><Lock size={16} /></div>
                  <div>
                    <strong>256-Bit Vault Key Envelope</strong>
                    <p>All items in your vault are encrypted with a random 256-bit symmetric vault key that is re-wrapped using your master key.</p>
                  </div>
                </div>

                <div className="guarantee-point">
                  <div className="point-icon"><Shield size={16} /></div>
                  <div>
                    <strong>Multi-Factor Verification</strong>
                    <p>Enhanced protection via TOTP 6-digit authenticator codes with offline cryptographic recovery fallback.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            TAB 3: PRIVACY & AUTO-LOCK
            ══════════════════════════════════════════════════════════ */}
        {activeTab === 'privacy' && (
          <div className="settings-tab-content">
            {/* Clipboard Sanitization Card */}
            <div className="settings-section-card glass-panel">
              <div className="settings-section-header">
                <div className="section-icon-badge" style={{ background: 'rgba(13, 148, 136, 0.12)', color: '#0d9488' }}>
                  <Clock size={22} />
                </div>
                <div>
                  <h3 className="section-title">Clipboard Memory Sanitization</h3>
                  <p className="section-subtitle">
                    Automatically wipes your operating system clipboard after copying sensitive credentials to prevent background snooping.
                  </p>
                </div>
              </div>

              <div className="settings-options-list">
                <div className="settings-option-item">
                  <div className="option-info">
                    <strong>Clipboard Auto-Clear Delay</strong>
                    <p>How long copied passwords, TOTP codes, and keys stay in your system clipboard buffer before being zeroized.</p>
                  </div>
                  <div className="option-control">
                    <select
                      className="admin-select"
                      value={clipboardClearTimeoutMs}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        setClipboardClearTimeoutMs(val);
                        showToast('Clipboard auto-clear timeout updated.');
                      }}
                    >
                      {clipboardOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="privacy-info-callout">
                <Info size={18} className="callout-icon" />
                <p>
                  When the timer expires, CipherVault overwrites the operating system clipboard with an empty string, ensuring other desktop applications cannot read historical password copies.
                </p>
              </div>
            </div>

            {/* Inactivity Auto-Lock Card */}
            <div className="settings-section-card glass-panel">
              <div className="settings-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div className="section-icon-badge" style={{ background: 'rgba(217, 119, 6, 0.12)', color: '#d97706' }}>
                    <Lock size={22} />
                  </div>
                  <div>
                    <h3 className="section-title">Vault Inactivity Auto-Lock</h3>
                    <p className="section-subtitle">
                      Automatically encrypts and seals your vault when your keyboard and mouse are idle.
                    </p>
                  </div>
                </div>

                {/* Active / Deactive iOS Toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <span style={{
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: autoLockEnabled ? '#10b981' : 'var(--text-secondary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em'
                  }}>
                    {autoLockEnabled ? 'Active' : 'Deactive'}
                  </span>
                  <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={autoLockEnabled}
                      onChange={handleToggleAutoLockEnabled}
                      style={{ display: 'none' }}
                    />
                    <div style={{
                      width: 46,
                      height: 26,
                      borderRadius: 14,
                      background: autoLockEnabled ? '#10b981' : 'rgba(255, 255, 255, 0.16)',
                      position: 'relative',
                      transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      boxShadow: autoLockEnabled ? '0 2px 10px rgba(16, 185, 129, 0.35)' : 'none',
                      border: autoLockEnabled ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: '#ffffff',
                        position: 'absolute',
                        top: 2,
                        left: autoLockEnabled ? 23 : 2,
                        transition: 'left 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.25)'
                      }} />
                    </div>
                  </label>
                </div>
              </div>

              <div className="settings-options-list" style={{
                opacity: autoLockEnabled ? 1 : 0.55,
                pointerEvents: autoLockEnabled ? 'auto' : 'none',
                transition: 'all 0.22s ease'
              }}>
                <div className="settings-option-item">
                  <div className="option-info">
                    <strong>Inactivity Auto-Lock Timeout</strong>
                    <p>
                      {autoLockEnabled
                        ? 'The duration of idle time before your local encryption session is discarded from browser memory.'
                        : 'Auto-lock is currently deactivated. Turn on the switch above to enable auto-locking on idle timeout.'}
                    </p>
                  </div>
                  <div className="option-control">
                    <select
                      className="admin-select"
                      value={autoLockTimeout}
                      onChange={handleAutoLockChange}
                      disabled={!autoLockEnabled}
                    >
                      <option value="1">1 minute (High Security)</option>
                      <option value="5">5 minutes</option>
                      <option value="15">15 minutes (Recommended)</option>
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Link to Per-Application Security Locks */}
            <div
              className="settings-section-card glass-panel"
              style={{ cursor: 'pointer', transition: 'all 0.2s ease', border: '1px solid rgba(16, 185, 129, 0.25)', marginBottom: '24px' }}
              onClick={() => setActiveTab('app-security')}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div className="section-icon-badge" style={{ background: 'rgba(16, 185, 129, 0.14)', color: '#10b981' }}>
                    <ShieldCheck size={22} />
                  </div>
                  <div>
                    <h3 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>Per-Application Security Locks</span>
                      <span style={{ fontSize: '0.72rem', background: 'rgba(16, 185, 129, 0.2)', color: '#10b981', padding: '2px 7px', borderRadius: '10px', fontWeight: 700 }}>
                        Independent
                      </span>
                    </h3>
                    <p className="section-subtitle">
                      Configure separate Biometrics, PIN, Pattern, or Password locks for Cards, Documents, Notes, and all apps.
                    </p>
                  </div>
                </div>
                <ChevronRight size={20} color="#10b981" />
              </div>
            </div>

            {/* Cache & Local State Purge */}
            <div className="settings-section-card glass-panel">
              <div className="settings-section-header">
                <div className="section-icon-badge" style={{ background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444' }}>
                  <RefreshCw size={22} />
                </div>
                <div>
                  <h3 className="section-title">Local Browser Memory & Cache Hygiene</h3>
                  <p className="section-subtitle">
                    Safely flush transient decrypted caches and offline keys from this browser instance.
                  </p>
                </div>
              </div>

              <div className="cache-purge-row">
                <div className="purge-copy">
                  <strong>Purge Local Storage Cache</strong>
                  <p>Deletes transient metadata and cached keys stored in localStorage without signing you out.</p>
                </div>
                <button type="button" className="btn-secondary" onClick={handlePurgeCaches}>
                  <RefreshCw size={15} /> Purge Local Cache
                </button>
              </div>
            </div>

            {/* Device Hardware & Privacy Permissions Card */}
            <div className="settings-section-card glass-panel">
              <div className="settings-section-header">
                <div className="section-icon-badge" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
                  <ShieldCheck size={22} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <h3 className="section-title" style={{ margin: 0 }}>Device & Hardware Permissions</h3>
                    <button
                      type="button"
                      className="btn-primary btn-sm"
                      onClick={() => window.dispatchEvent(new CustomEvent('open-device-consent'))}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '6px 14px' }}
                    >
                      <ShieldCheck size={14} /> Configure Permissions
                    </button>
                  </div>
                  <p className="section-subtitle" style={{ marginTop: 4 }}>
                    Manage browser permissions for Camera, Microphone, Geolocation, and Push Notifications. All device access is strictly consent-based and under your full control.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            TAB 4: VAULT BACKUP & EXPORT
            ══════════════════════════════════════════════════════════ */}
        {activeTab === 'data' && (
          <div className="settings-tab-content">
            {/* Export Vault Backup Card */}
            <div className="settings-section-card glass-panel">
              <div className="settings-section-header">
                <div className="section-icon-badge" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
                  <Download size={22} />
                </div>
                <div>
                  <h3 className="section-title">Export Vault Backup</h3>
                  <p className="section-subtitle">
                    Download an offline copy of all items in your personal vault. Available in structured JSON or plaintext CSV.
                  </p>
                </div>
              </div>

              <div className="export-banner-box">
                <div className="export-banner-left">
                  <FileText size={26} color="#3b82f6" />
                  <div>
                    <strong>Create an Offline Backup of Your Vault</strong>
                    <p>Keep a secure snapshot of your logins, cards, and secure notes on external storage.</p>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="btn-primary"
                  onClick={() => { setExportError(''); setExportModalOpen(true); }}
                >
                  <Download size={16} /> Export Vault Records
                </button>
              </div>
            </div>

            {/* Import Data Card */}
            <div className="settings-section-card glass-panel">
              <div className="settings-section-header">
                <div className="section-icon-badge" style={{ background: 'rgba(139, 92, 246, 0.12)', color: '#8b5cf6' }}>
                  <Upload size={22} />
                </div>
                <div>
                  <h3 className="section-title">Import from External Password Managers</h3>
                  <p className="section-subtitle">
                    Migrate credentials seamlessly from Bitwarden, 1Password, LastPass, or Google Chrome.
                  </p>
                </div>
              </div>

              <div className="import-providers-grid">
                <div className="import-provider-pill">
                  <div className="provider-logo bitwarden" />
                  <span>Bitwarden (.json / .csv)</span>
                </div>
                <div className="import-provider-pill">
                  <div className="provider-logo onepassword" />
                  <span>1Password (.1pif / .csv)</span>
                </div>
                <div className="import-provider-pill">
                  <div className="provider-logo chrome" />
                  <span>Chrome Passwords (.csv)</span>
                </div>
                <div className="import-provider-pill">
                  <div className="provider-logo lastpass" />
                  <span>LastPass Export (.csv)</span>
                </div>
              </div>

              <div style={{ marginTop: '16px' }}>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setImportModalOpen(true)}
                >
                  <Upload size={15} /> Import Credentials
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            TAB 5: SHORTCUTS & DANGER ZONE
            ══════════════════════════════════════════════════════════ */}
        {activeTab === 'shortcuts' && (
          <div className="settings-tab-content">
            {/* Quick Links Grid */}
            <div className="settings-section-card glass-panel">
              <div className="settings-section-header">
                <div className="section-icon-badge" style={{ background: 'rgba(13, 148, 136, 0.12)', color: '#0d9488' }}>
                  <Sparkles size={22} />
                </div>
                <div>
                  <h3 className="section-title">Quick System Shortcuts</h3>
                  <p className="section-subtitle">Direct shortcuts to specialized security, hardware, and admin modules.</p>
                </div>
              </div>

              <div className="shortcuts-nav-grid">
                <div className="shortcut-card" onClick={() => navigate('/security')}>
                  <div className="shortcut-icon" style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10b981' }}>
                    <ShieldCheck size={24} />
                  </div>
                  <div className="shortcut-details">
                    <strong>Zero-Knowledge Security Center</strong>
                    <p>Two-Factor Authentication, Master Password change, and live audit log.</p>
                  </div>
                  <ChevronRight size={18} className="chevron" />
                </div>

                <div className="shortcut-card" onClick={() => navigate('/devices')}>
                  <div className="shortcut-icon" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
                    <Smartphone size={24} />
                  </div>
                  <div className="shortcut-details">
                    <strong>Connected Devices</strong>
                    <p>Review authorized mobile apps, browser extensions, and laptops.</p>
                  </div>
                  <ChevronRight size={18} className="chevron" />
                </div>

                <div className="shortcut-card" onClick={() => navigate('/sessions')}>
                  <div className="shortcut-icon" style={{ background: 'rgba(139, 92, 246, 0.12)', color: '#8b5cf6' }}>
                    <Monitor size={24} />
                  </div>
                  <div className="shortcut-details">
                    <strong>Active User Sessions</strong>
                    <p>Monitor IP geolocation, active JWT tokens, and revoke remote access.</p>
                  </div>
                  <ChevronRight size={18} className="chevron" />
                </div>

                {['SUPER_ADMIN', 'ADMIN'].includes(user?.role) && (
                  <div className="shortcut-card" onClick={() => navigate('/admin/users')}>
                    <div className="shortcut-icon" style={{ background: 'rgba(217, 119, 6, 0.12)', color: '#d97706' }}>
                      <User size={24} />
                    </div>
                    <div className="shortcut-details">
                      <strong>Admin Users Control Center</strong>
                      <p>Add, edit, suspend, or delete user accounts and permissions.</p>
                    </div>
                    <ChevronRight size={18} className="chevron" />
                  </div>
                )}
              </div>
            </div>

            {/* Danger Zone */}
            <div className="settings-section-card glass-panel danger-zone-card">
              <div className="settings-section-header">
                <div className="section-icon-badge danger-icon">
                  <AlertTriangle size={22} />
                </div>
                <div>
                  <h3 className="section-title text-danger">Emergency Danger Zone</h3>
                  <p className="section-subtitle">High-risk actions that immediately sever active sessions and memory keys.</p>
                </div>
              </div>

              <div className="danger-actions-list">
                <div className="danger-item-row">
                  <div>
                    <strong>Lock Vault Immediately</strong>
                    <p>Erases the active 256-bit vault key from memory. Re-entering your master password is required to reopen.</p>
                  </div>
                  <button type="button" className="btn-danger-outline" onClick={logout}>
                    <Lock size={15} /> Lock Vault
                  </button>
                </div>

                <div className="danger-item-row">
                  <div>
                    <strong>Sign Out of All Other Devices</strong>
                    <p>Revokes all active refresh tokens on all remote phones, tablets, and browsers.</p>
                  </div>
                  <button type="button" className="btn-danger" onClick={() => navigate('/security')}>
                    <LogOut size={15} /> Manage in Security
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            TAB 6: APPLICATIONS & PINNED
            ══════════════════════════════════════════════════════════ */}
        {activeTab === 'apps' && (
          <div className="settings-tab-content">
            <PinnedAppsSettings />
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            TAB: APPLICATION SECURITY
            ══════════════════════════════════════════════════════════ */}
        {activeTab === 'app-security' && (
          <div className="settings-tab-content">
            <ApplicationSecuritySettings />
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            TAB 7: IMPORT & EXPORT
            ══════════════════════════════════════════════════════════ */}
        {activeTab === 'import' && (
          <div className="settings-tab-content">
            <ImportWizard onImportComplete={() => showToast('Import completed successfully!')} />
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            TAB 8: IMPORT HISTORY
            ══════════════════════════════════════════════════════════ */}
        {activeTab === 'history' && (
          <div className="settings-tab-content">
            <ImportHistoryTable />
          </div>
        )}

        {/* ─── Export Modal ─── */}
        {exportModalOpen && (
          <div className="modal-backdrop" onClick={() => setExportModalOpen(false)}>
            <div className="modal-card glass-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '540px' }}>
              <div className="modal-header">
                <div className="modal-header-icon" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
                  <Download size={20} />
                </div>
                <div>
                  <h3>Export Vault Data</h3>
                  <p>Choose your preferred backup format</p>
                </div>
                <button className="modal-close" onClick={() => setExportModalOpen(false)} aria-label="Close modal">
                  <X size={18} />
                </button>
              </div>

              <div className="modal-body-content">
                {exportError && (
                  <div className="admin-toast admin-toast-error">
                    <AlertTriangle size={16} />
                    <span>{exportError}</span>
                  </div>
                )}

                <div className="export-format-selector">
                  <label 
                    className={`format-radio-card ${exportFormat === 'encrypted' ? 'selected' : ''}`}
                    onClick={() => setExportFormat('encrypted')}
                  >
                    <div className="format-radio-top">
                      <input 
                        type="radio" 
                        name="exportFormat" 
                        value="encrypted" 
                        checked={exportFormat === 'encrypted'} 
                        onChange={() => setExportFormat('encrypted')} 
                      />
                      <strong>Encrypted CipherVault Backup (.json)</strong>
                    </div>
                    <p>Highest security. Zero plaintext secrets. Retains ciphertext blobs encrypted with AES-256-GCM.</p>
                  </label>

                  <label 
                    className={`format-radio-card ${exportFormat === 'json' ? 'selected' : ''}`}
                    onClick={() => setExportFormat('json')}
                  >
                    <div className="format-radio-top">
                      <input 
                        type="radio" 
                        name="exportFormat" 
                        value="json" 
                        checked={exportFormat === 'json'} 
                        onChange={() => setExportFormat('json')} 
                      />
                      <strong>Plaintext Structured JSON (.json)</strong>
                    </div>
                    <p>Decrypted structured format containing logins, folders, notes, and metadata.</p>
                  </label>

                  <label 
                    className={`format-radio-card ${exportFormat === 'csv' ? 'selected' : ''}`}
                    onClick={() => setExportFormat('csv')}
                  >
                    <div className="format-radio-top">
                      <input 
                        type="radio" 
                        name="exportFormat" 
                        value="csv" 
                        checked={exportFormat === 'csv'} 
                        onChange={() => setExportFormat('csv')} 
                      />
                      <strong>Plaintext Standard CSV (.csv)</strong>
                    </div>
                    <p>Spreadsheet compatible. Ideal for spreadsheet viewers or external migrations.</p>
                  </label>
                </div>

                {(exportFormat === 'json' || exportFormat === 'csv') && (
                  <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div className="delete-warning-box" style={{ background: 'rgba(245, 158, 11, 0.08)', borderColor: 'rgba(245, 158, 11, 0.3)' }}>
                      <AlertTriangle size={18} color="#f59e0b" style={{ flexShrink: 0 }} />
                      <div>
                        <strong style={{ color: '#f59e0b' }}>Plaintext Export Warning</strong>
                        <p>This export may contain plaintext secrets.</p>
                      </div>
                    </div>

                    <div className="form-group" style={{ marginTop: '4px' }}>
                      <label htmlFor="step-up-pass">Confirm Master Password to Authorize Export</label>
                      <input
                        id="step-up-pass"
                        type="password"
                        className="input-field"
                        placeholder="Enter your master password"
                        value={stepUpPassword}
                        onChange={(e) => setStepUpPassword(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setExportModalOpen(false)} disabled={exporting}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleExecuteExport}
                  disabled={exporting || ((exportFormat === 'json' || exportFormat === 'csv') && !stepUpPassword)}
                >
                  {exporting ? 'Authorizing & Exporting...' : 'Download Vault Backup'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Import Guidance Modal ─── */}
        {importModalOpen && (
          <div className="modal-backdrop" onClick={() => setImportModalOpen(false)}>
            <div className="modal-card glass-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
              <div className="modal-header">
                <div className="modal-header-icon" style={{ background: 'rgba(139, 92, 246, 0.12)', color: '#8b5cf6' }}>
                  <Upload size={20} />
                </div>
                <div>
                  <h3>Import Vault Items</h3>
                  <p>Import from external password managers</p>
                </div>
                <button className="modal-close" onClick={() => setImportModalOpen(false)} aria-label="Close modal">
                  <X size={18} />
                </button>
              </div>

              <div className="modal-body-content">
                <p>To import existing credentials into CipherVault:</p>
                <ol style={{ paddingLeft: '20px', lineHeight: '1.7', color: 'var(--text-secondary)' }}>
                  <li>Export your passwords as a <strong>CSV file</strong> from your existing manager (e.g. Bitwarden or Google Chrome).</li>
                  <li>In CipherVault, navigate to <strong>Vault</strong> &gt; <strong>Add Item</strong>.</li>
                  <li>Select bulk upload or drop your file into the import dropzone.</li>
                </ol>

                <div className="notice-box-info" style={{ marginTop: '12px' }}>
                  <ShieldCheck size={16} color="#0d9488" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <p>All imported credentials will be automatically re-encrypted on your device using your AES-256-GCM vault key before being synced.</p>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-primary" onClick={() => setImportModalOpen(false)}>
                  Got It
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
